import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { FilterModel, LocationValue } from 'ts/hotel/classes/FilterModel';
import Autocomplete from 'react-google-autocomplete';
import { useTranslation } from 'react-i18next';
import MovablePlaceholder from 'components/ui/MovablePlaceholder/MovablePlaceholder';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import FilterByKm from './FilterByKm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLocationArrow,
  faLocationDot,
  faMapPin,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useSearchParams } from 'react-router-dom';
import { getDistanceByCoords } from 'utils/utils';
import { getGoogleAutocomplete } from 'services/hotel';
import { MapHotelContext } from 'contexts/MapHotelContext';

interface FilterResultLocationProps {
  onLocationChange: (location: LocationValue) => void;
  onFilterChange: (filter: FilterModel) => void;
  filter: FilterModel;
}

const FilterResultLocation: FC<FilterResultLocationProps> = (props) => {
  const [t] = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const { resetLocation, centered } = useContext(MapHotelContext);
  const [urlParams] = useSearchParams();
  const ref = useRef<any>(null);
  const [activeSwitcher, setActiveSwitcher] = useState(0);
  const [inputVal, setInutVal] = useState('');
  const reqRef = useRef(new AbortController());
  const [options, setOptions] = useState<Select[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    // onClickReset();
  }, [resetLocation]); //eslint-disable-line

  useEffect(() => {
    if (centered) {
      setInutVal(centered.name || '');
      setOptions([{ value: centered.name || '', label: centered.name || '' }]);
    }
  }, [centered]);

  const onClickReset = () => {
    setTimeout(() => {
      if (ref.current) {
        ref.current.value = '';

        const event = new Event('input', { bubbles: true });
        ref.current.dispatchEvent(event);
      }
    });
    setInutVal('');
    setOptions([]);
    props.onLocationChange({ lat: 0, lng: 0, name: '' });
  };

  const onChangeGoogle = async (val: string) => {
    reqRef.current.abort();
    reqRef.current = new AbortController();

    if (val) {
      setInutVal(val);
    }
    if (val.length > 1) {
      setTimeout(() => {
        setIsLoading(true);
      });
      const params = {
        latitude: +(urlParams.get('lat') || ''),
        longitude: +(urlParams.get('lon') || ''),
        query: `${val}, ${urlParams.get('loc')} (and vicinity)`,
        radius: 50000,
      };
      const lat = +(urlParams.get('lat') || '');
      const lon = +(urlParams.get('lon') || '');
      const params2 = {
        latitude: lat,
        longitude: lon,
        query: `${val}, ${urlParams.get('loc')}`,
        radius: 50000,
      };

      const data = await getGoogleAutocomplete(params, reqRef.current.signal);
      const data2 = await getGoogleAutocomplete(params2, reqRef.current.signal);
      const opt = [...data, ...data2]
        .sort((a, b) => b.name?.localeCompare(a.name))
        .filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              (t) => t.name === item.name && t.formatted_address === item.formatted_address
            )
        )
        .map((o) => ({
          value: o.name,
          label: o.name,
          data: {
            ...o.geometry.location,
          },
          html: (
            <div className="hotel-loc flex-column">
              <div className="flex-between">
                <div className="flex-row mb-5" style={{ flexWrap: 'nowrap' }}>
                  <img alt="1" src={o.icon} className="mr-5" style={{ width: 15, height: 15 }} />
                  <span>{o.name}</span>
                </div>
                <span className="text-xsm">
                  {getDistanceByCoords(o.geometry.location.lat, o.geometry.location.lng, lat, lon)}
                  {t('km')}
                </span>
              </div>
              <div className="text-light flex-row" style={{ flexWrap: 'nowrap' }}>
                <FontAwesomeIcon icon={faLocationDot} className="mr-5" />
                <span>{o.formatted_address}</span>
              </div>
            </div>
          ),
        }));

      setOptions(opt);
      setIsMenuOpen(!!opt.length);
      setIsLoading(false);
    } else if (val.length > 0) {
      setOptions([]);
      setIsMenuOpen(false);
    }
  };

  const onSelectGoogle = async (val: string) => {
    setInutVal(val);
    setIsMenuOpen(false);

    const opt = options.find((t) => t.value === val);
    props.onLocationChange({ name: val, lat: opt?.data.lat, lng: opt?.data.lng });
  };
  return (
    <div className="hotel-result-filter-input location position-relative" data-assistant-target="location">
      {(!!ref.current?.value || !!inputVal) && (
        <FontAwesomeIcon
          onClick={onClickReset}
          icon={faTimes}
          className="hotel-result-filter-clearsm"
        />
      )}
      <FilterByKm onFilterChange={props.onFilterChange} filter={props.filter} />
      <div className="form-control-switchers">
        <div
          className={'form-control-switcher' + (activeSwitcher === 0 ? ' active' : '')}
          onClick={() => setActiveSwitcher(0)}
        >
          <FontAwesomeIcon icon={faMapPin} />
        </div>
        <div
          className={'form-control-switcher' + (activeSwitcher === 1 ? ' active' : '')}
          onClick={() => setActiveSwitcher(1)}
        >
          <FontAwesomeIcon icon={faLocationArrow} />
        </div>
      </div>
      <div className="position-relative location-input">
        {activeSwitcher === 0 && (
          <Fragment>
            <SelectCustom
              value={inputVal}
              onChange={onSelectGoogle}
              onInputChange={onChangeGoogle}
              options={options}
              placeholder={t('hotel.loca')}
              filterOption={() => true}
              menuIsOpen={isMenuOpen}
              isLoading={isLoading}
            />
          </Fragment>
        )}
        {activeSwitcher === 1 && (
          <Fragment>
            <MovablePlaceholder
              label={t('hotel.loca')}
              showSmall={isFocused || !!ref.current?.value}
              emptyIcon={true}
            />

            <Autocomplete
              ref={ref}
              className="form-control movable-placeholder-input movable-placeholder-input-noicon"
              onPlaceSelected={(val, input: any) => {
                const value = {
                  lat: val.geometry?.location?.lat() || 0,
                  lng: val.geometry?.location?.lng() || 0,
                  name: input.value || '',
                };
                ref.current.value = input.value;
                props.onLocationChange(value);
              }}
              language="en"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              options={{
                types: ['address'],
                bounds: (() => {
                  const circle = new google.maps.Circle({
                    center: new google.maps.LatLng(
                      +(urlParams.get('lat') || '0'),
                      +(urlParams.get('lon') || '0')
                    ),
                    radius: 50000,
                  });

                  return circle.getBounds() || undefined;
                })(),
              }}
            />
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(FilterResultLocation, 'FilterResultLocation');
